<template>
  <div>
    <h2 class="mt-0">
      Your progress
    </h2>
    <h3 class="athlete-goal p-1 text-right text-muted col-12 mb-0 mt-0 row justify-content-between">
      {{ user.username }}
      <span>
        <strong>
          {{ computedTotal }}</strong> out of <strong>{{ challenge.challenge_type === 'elevation' ? formatNumberWithCommas(goalValue) : goalValue }}
          </strong>
        {{ formatUnit(challenge.challenge_type) }}
      </span>
    </h3>
    <span class="row bar col-12 pl-0 pr-0">
      <span class="bar-deets">{{ progress >= 100 ? "100%" : parseFloat(progress).toFixed(2) + "%" }}</span>
      <span v-if="progress < 100" :class="['bar-target', onTarget ? 'on-target' : 'off-target']">
        {{ onTarget ? 'On target' : 'Keep trying' }}
      </span>
      <span class="bar-width d-block" :style="{ width: progress + '%' }"></span>
    </span>
    <span class="row mt-2 w-100 d-flex justify-content-end">
      <span v-if="showUpdateForm" :class="['mb-2 justify-content-end', !hasGoal ? 'mb-sm-2 card card__red w-100' : 'row mb-sm-0']">
        <h2 v-if="hasGoal" class="text-right d-none d-sm-block mt-1 mb-0 mr-1">Goal:</h2>
        <span v-show="!hasGoal">
          <h2 class="m-0">Your goal</h2>
          <p class="mb-2">You can set your own personal goal for this challenge here. Make it a challenge, but achievable. Once you're happy click the update button.</p>
        </span>
        <input id="goal" type="number" :value="goalValue" />
        <button class="btn btn-primary__slim ml-2" @click="updateGoal">UPDATE</button>
      </span>
      <button v-if="!showUpdateForm" class="btn btn-primary__white mb-2 mb-sm-0" @click="showUpdateForm = true">CHANGE GOAL</button>
    </span>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { getTotal, getProgress, getUnit, numberWithCommas } from '../composables/useFormatters.js';
import { athleteTotals } from '../composables/useAthleteData.js';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  challenge: {
    type: Object,
    required: true,
  },
  activities: {
    type: Array,
    required: true,
  },
  goal: {
    type: Number,
    required: false,
    default: 0,
  },
  hasGoal: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const athleteTotalsAll = athleteTotals(props.activities);
const showUpdateForm = ref(!props.hasGoal);
const progress = ref(0);
const goalValue = ref(props.goal || props.challenge.default_goal);

const today = new Date();
const oneDay = 1000 * 60 * 60 * 24;
const daysLeft = Math.ceil((new Date(props.challenge.ends).getTime() - today.getTime()) / oneDay);
const percDaysLeft = (daysLeft / 365) * 100;

const onTarget = computed(() => percDaysLeft > (100 - progress.value));

const computedTotal = computed(() => {
  if (athleteTotalsAll.length > 0) {
    return getTotal(athleteTotalsAll[0], props.challenge.challenge_type);
  }
  return '0';
});

onMounted(() => {
  const target = props.goal || props.challenge.default_goal;
  if (athleteTotalsAll.length > 0) {
    progress.value = getProgress(target, props.challenge.challenge_type, athleteTotalsAll[0]);
  }
});

function formatUnit(challengeType) {
  return getUnit(challengeType);
}

function formatNumberWithCommas(n) {
  return numberWithCommas(n);
}

function updateGoal() {
  const value = document.getElementById('goal').value;
  axios
    .post('/update-goal', {
      challenge_id: props.challenge.id,
      user_id: props.user.id,
      goal: value,
    })
    .then(() => {
      location.reload();
    })
    .catch(e => {
      console.log(e);
    });
}
</script>

<style lang="scss" scoped>
.athlete-name {
  text-transform: capitalize;
}

.bar {
  position: relative;
  background: white;
  border-radius: 25px;
  overflow: hidden;
  .bar-name, .bar-miles {
    position: absolute;
    left: 48%;
    top: 12px;
    text-transform: capitalize;
    color: #243546;
    font-weight: 700;
  }
  .bar-deets {
    position: absolute;
    right: 2px;
    top: 3px;
    text-transform: capitalize;
    color: #174776;
    display: inline-block;
    background: rgba(255, 255, 255, 0.8);
    padding: 6px;
    border-radius: 16px;
    font-size: 14px;
  }
  .bar-name {
    right: auto;
    left: 3px;
    top: 6px;
    font-weight: 500;
    background: #ffffff;
    padding: 5px 10px;
    border-radius: 4px;
    border: 1px solid #d3e3ef
  }
  .bar-miles {
    left: auto;
    right: 3px;
    font-weight: 500;
  }
  .bar-width {
    background-color: #174776;
    height: 31px;
    border-radius: 25px;
  }
}

.bar-target {
  position: absolute;
  left: 3px;
  top: 3px;
  color: #174776;
  display: inline-block;
  background: rgba(255, 255, 255, 0.8);
  padding: 6px;
  border-radius: 16px;
  font-size: 14px;

  &.on-target {
    color: #02a517
  }
}

.bar-digits {
  color: #243546;
  font-weight: 700;
  height: 40px;
  padding-top: 12px;
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  color: white;
}

.mvp {
  color: #02a517;
}
</style>
